import { doc, onSnapshot } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onIdTokenChanged, type User } from 'firebase/auth';
import { db } from '../lib/firebase';
import type { TournamentPlatformConfig } from '../types';

export const DEFAULT_TOURNAMENT_CONFIG: TournamentPlatformConfig = {
  schemaVersion: 1,
  adminPreviewEnabled: true,
  publicAccessEnabled: false,
  creationEnabled: true,
  registrationEnabled: false,
  shellEntryEnabled: false,
  submissionEnabled: false,
};

const functions = getFunctions(getApp(), 'us-central1');
const setTournamentConfigCallable = httpsCallable<Partial<TournamentPlatformConfig>, { ok: true }>(
  functions,
  'adminSetTournamentConfig',
);

export function subscribeTournamentConfig(
  onUpdate: (config: TournamentPlatformConfig) => void,
): () => void {
  const auth = getAuth(getApp());
  let cancelled = false;
  let configUnsubscribe: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let attachGeneration = 0;

  const clearRetry = () => {
    if (!retryTimer) return;
    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const detachConfig = () => {
    if (!configUnsubscribe) return;
    try {
      configUnsubscribe();
    } catch {}
    configUnsubscribe = null;
  };

  const scheduleRetry = (user: User, delayMs = 500) => {
    if (cancelled || retryTimer) return;

    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (cancelled) return;

      const current = auth.currentUser;
      if (!current?.uid || current.uid !== user.uid) return;

      void attachConfig(current, true);
    }, delayMs);
  };

  const attachConfig = async (user: User, forceRefresh = false) => {
    if (cancelled || configUnsubscribe) return;

    const generation = ++attachGeneration;

    try {
      // Firestore rules require request.auth != null. Waiting for a real ID token
      // makes anonymous web sessions deterministic instead of racing auth hydration.
      await user.getIdToken(forceRefresh);
    } catch (error) {
      console.warn('[tournamentConfig] token hydration failed', error);
    }

    if (cancelled || generation !== attachGeneration || configUnsubscribe) return;
    if (!auth.currentUser?.uid || auth.currentUser.uid !== user.uid) return;

    console.debug('[tournamentConfig] attaching', {
      uid: user.uid,
      isAnonymous: user.isAnonymous,
      forceRefresh,
    });

    configUnsubscribe = onSnapshot(
      doc(db, 'meta', 'tournamentConfig'),
      (snapshot) => {
        clearRetry();
        const next = {
          ...DEFAULT_TOURNAMENT_CONFIG,
          ...(snapshot.exists() ? snapshot.data() : {}),
        } as TournamentPlatformConfig;

        console.debug('[tournamentConfig] loaded', {
          uid: auth.currentUser?.uid ?? null,
          publicAccessEnabled: next.publicAccessEnabled,
          registrationEnabled: next.registrationEnabled,
          submissionEnabled: next.submissionEnabled,
        });

        onUpdate(next);
      },
      (error: any) => {
        console.error('[tournamentConfig] subscription failed', {
          code: error?.code,
          message: error?.message,
          uid: auth.currentUser?.uid ?? null,
          isAnonymous: auth.currentUser?.isAnonymous ?? null,
        });

        configUnsubscribe = null;
        if (cancelled) return;

        if (error?.code === 'permission-denied') {
          const current = auth.currentUser;
          if (current?.uid) scheduleRetry(current, 500);
          return;
        }

        onUpdate(DEFAULT_TOURNAMENT_CONFIG);
      },
    );
  };

  const authUnsubscribe = onIdTokenChanged(auth, (user) => {
    if (cancelled) return;

    clearRetry();
    detachConfig();
    attachGeneration += 1;

    if (!user) {
      onUpdate(DEFAULT_TOURNAMENT_CONFIG);
      return;
    }

    void attachConfig(user, false);
  });

  if (auth.currentUser) {
    void attachConfig(auth.currentUser, false);
  }

  return () => {
    cancelled = true;
    attachGeneration += 1;
    clearRetry();
    authUnsubscribe();
    detachConfig();
  };
}

export async function cfAdminSetTournamentConfig(
  patch: Partial<TournamentPlatformConfig>,
) {
  return setTournamentConfigCallable(patch);
}
